import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { IoClose, IoSearchOutline } from "react-icons/io5";

import { WorkTypeModel } from "../../../../factories/work-type/models/work-type.model";
import { AssignWorkTypesPanelProps } from "../model/statuses.types";

export const AssignWorkTypesPanel: React.FC<AssignWorkTypesPanelProps> = ({
  subStatus,
  workTypes,
  selectedWorkTypeIds,
  isSaving,
  onClose,
  onSave,
}) => {
  const [search, setSearch] = useState("");
  const [draftIds, setDraftIds] = useState<string[]>(selectedWorkTypeIds);

  useEffect(() => {
    setDraftIds(selectedWorkTypeIds);
    setSearch("");
  }, [selectedWorkTypeIds, subStatus.id]);

  const visibleWorkTypes = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return workTypes.filter((workType) =>
      normalizedSearch
        ? workType.name.toLowerCase().includes(normalizedSearch)
        : true,
    );
  }, [search, workTypes]);

  const visibleWorkTypeIds = visibleWorkTypes.map((workType) => workType.id);
  const isAllVisibleSelected =
    visibleWorkTypeIds.length > 0 &&
    visibleWorkTypeIds.every((id) => draftIds.includes(id));

  const toggleWorkType = (workType: WorkTypeModel) => {
    setDraftIds((prev) =>
      prev.includes(workType.id)
        ? prev.filter((id) => id !== workType.id)
        : [...prev, workType.id],
    );
  };

  const toggleSelectAll = () => {
    if (isAllVisibleSelected) {
      setDraftIds((prev) =>
        prev.filter((id) => !visibleWorkTypeIds.includes(id)),
      );
      return;
    }

    setDraftIds((prev) =>
      Array.from(new Set([...prev, ...visibleWorkTypeIds])),
    );
  };

  const handleDone = async () => {
    await onSave(subStatus.id, draftIds);
    onClose();
  };

  return (
    <div className={"px-4 pb-4"}>
      <div
        className={
          "rounded-[10px] border border-[#E0DBF5] bg-[#F8F7FE] px-[13px] py-3"
        }>
        <div
          className={
            "mb-[9px] text-[11px] font-extrabold uppercase tracking-[0.5px] text-[#6B4EFF]"
          }>
          Show in work types
        </div>

        <div
          className={
            "mb-[9px] flex items-center gap-[7px] rounded-lg border-[1.5px] border-[#E0DBF5] bg-white px-[9px] py-[7px]"
          }>
          <IoSearchOutline className={"h-3.5 w-3.5 shrink-0 text-[#9097A8]"} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={"Search work types..."}
            className={
              "min-w-0 flex-1 bg-transparent font-Manrope text-[13px] text-[#1F2433] outline-none placeholder:text-[#9097A8]"
            }
          />
          {search && (
            <button
              type={"button"}
              onClick={() => setSearch("")}
              className={
                "flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#ECEAF6] text-[#6B7185]"
              }>
              <IoClose className={"h-2.5 w-2.5"} />
            </button>
          )}
        </div>

        <div className={"max-h-[168px] overflow-y-auto"}>
          <button
            type={"button"}
            onClick={toggleSelectAll}
            className={
              "mb-[3px] flex w-full items-center gap-[9px] border-b border-[#E8E3F5] px-0.5 py-1.5 text-left"
            }>
            <CheckboxIcon checked={isAllVisibleSelected} />
            <span className={"text-[13px] font-bold text-[#6B4EFF]"}>
              Select all
            </span>
          </button>

          {visibleWorkTypes.length === 0 ? (
            <div className={"px-0.5 py-2.5 text-[12.5px] text-[#9097A8]"}>
              No work types found.
            </div>
          ) : (
            visibleWorkTypes.map((workType) => {
              const isSelected = draftIds.includes(workType.id);

              return (
                <button
                  key={workType.id}
                  type={"button"}
                  onClick={() => toggleWorkType(workType)}
                  className={
                    "flex w-full items-center gap-[9px] px-0.5 py-1.5 text-left"
                  }>
                  <CheckboxIcon checked={isSelected} />
                  <span
                    className={
                      "min-w-0 truncate text-[13px] font-medium text-[#3A4256]"
                    }>
                    {workType.name}
                  </span>
                </button>
              );
            })
          )}
        </div>

        <button
          type={"button"}
          onClick={() => void handleDone()}
          disabled={isSaving}
          className={classNames(
            "mt-2.5 w-full rounded-[7px] border-none bg-[#6B4EFF] p-2 text-[12.5px] font-bold text-white",
            isSaving ? "cursor-not-allowed opacity-70" : "cursor-pointer",
          )}>
          Done
        </button>
      </div>
    </div>
  );
};

const CheckboxIcon: React.FC<{ checked: boolean }> = ({ checked }) => (
  <span
    className={classNames(
      "flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-[5px] border-[1.5px]",
      checked ? "border-[#6B4EFF] bg-[#6B4EFF]" : "border-[#CDD3DE] bg-white",
    )}>
    {checked && (
      <span
        className={
          "h-2.5 w-1.5 rotate-45 border-b-[2.5px] border-r-[2.5px] border-white"
        }
      />
    )}
  </span>
);
